import { useEffect, useMemo, useState } from "react";
import type { Application } from "../../types";
import { computeKpis, fetchLeadsTable } from "../../kpi";

/** District map artwork (PNG with dark surround — CSS screen blend knocks black into the sunburst) */
const telanganaMapSrc = new URL("../../assets/telangana-state-map.png", import.meta.url).href;

/** Pastel badge + darker building glyph (reference “secretariat” style tiles) */
const ICON_STYLES = [
  { soft: "#d1f4ea", dark: "#0f766e" },
  { soft: "#dbeafe", dark: "#1d4ed8" },
  { soft: "#fce7f3", dark: "#be185d" },
  { soft: "#fef3c7", dark: "#b45309" },
] as const;

function formatIn(n: number): string {
  return new Intl.NumberFormat("en-IN").format(n);
}

function MetricIcon({ soft, dark }: { soft: string; dark: string }) {
  return (
    <span className="dash-key-metrics__icon-badge" style={{ background: soft }} aria-hidden>
      <svg className="dash-key-metrics__icon-svg" viewBox="0 0 24 24" width="20" height="20">
        <path
          fill={dark}
          d="M12 3L4 8v12h4v-7h8v7h4V8l-8-5zm0 2.2l5.5 3.4V8H6.5V8.6L12 5.2zM9 13h2v2H9v-2zm4 0h2v2h-2v-2z"
        />
      </svg>
    </span>
  );
}

export function DashboardKeyMetrics({ applications }: { applications: Application[] }) {
  const k = useMemo(() => computeKpis(applications), [applications]);
  const [totalLeads, setTotalLeads] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchLeadsTable()
      .then((rows) => {
        if (!cancelled) setTotalLeads(rows.length);
      })
      .catch(() => {
        if (!cancelled) setTotalLeads(0);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const items = useMemo(
    () => [
      {
        value: totalLeads === null ? "—" : formatIn(totalLeads),
        label: "Total Leads",
        icon: ICON_STYLES[0],
      },
      { value: formatIn(k.totalReceived), label: "Total Applicants", icon: ICON_STYLES[1] },
      { value: formatIn(k.pendingValidation), label: "HO Verification Pending", icon: ICON_STYLES[2] },
      { value: formatIn(k.subcontractorLinked), label: "Subcontractor linked", icon: ICON_STYLES[3] },
    ],
    [k, totalLeads],
  );

  return (
    <section className="dash-key-metrics" aria-labelledby="dash-key-metrics-heading">
      <div className="dash-key-metrics__bg" aria-hidden>
        <div className="dash-key-metrics__sunburst" />
        <div className="dash-key-metrics__rays" />
        <img
          src={telanganaMapSrc}
          alt=""
          className="dash-key-metrics__map dash-key-metrics__map--state-png"
          decoding="async"
        />
      </div>

      <div className="dash-key-metrics__inner">
        <div className="dash-key-metrics__ribbon-badge">
          <h2 id="dash-key-metrics-heading" className="dash-key-metrics__ribbon">
            Our key metrics
          </h2>
        </div>

        <ul className="dash-key-metrics__grid">
          {items.map((row) => (
            <li key={row.label} className="dash-key-metrics__card">
              <MetricIcon soft={row.icon.soft} dark={row.icon.dark} />
              <p className="dash-key-metrics__value">{row.value}</p>
              <p className="dash-key-metrics__label">{row.label}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
